export const STORE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const RESERVED_STOREFRONT_SUBDOMAINS = [
  'account',
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'billing',
  'builder-canvas',
  'cart',
  'cdn',
  'checkout',
  'dashboard',
  'help',
  'login',
  'mail',
  'preview',
  'signin',
  'static',
  'status',
  'support',
  'www',
] as const;

const RESERVED_STOREFRONT_SUBDOMAIN_SET = new Set<string>(RESERVED_STOREFRONT_SUBDOMAINS);

export type StorefrontAddress =
  | {
      readonly kind: 'tenant';
      readonly slug: string;
      readonly pathWithoutStorePrefix: string;
    }
  | {
      readonly kind: 'legacy';
      readonly slug: string;
      readonly pathWithoutStorePrefix: string;
    }
  | { readonly kind: 'platform' }
  | { readonly kind: 'invalid' };

export interface ResolveStorefrontAddressInput {
  readonly url: string | URL;
  readonly baseDomain: string;
}

export interface StorefrontUrlParts {
  readonly storeSlug: string;
  readonly path?: string;
  readonly search?: string | URLSearchParams | null;
  readonly hash?: string | null;
}

export function hasControlOrBidi(value: string): boolean {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f) return true;
    if (code >= 0x202a && code <= 0x202e) return true;
    if (code >= 0x2066 && code <= 0x2069) return true;
  }
  return false;
}

function stripTrailingDot(value: string): string {
  return value.endsWith('.') ? value.slice(0, -1) : value;
}

function normalizeHostname(value: string): string {
  return stripTrailingDot(value.trim().toLowerCase());
}

function ensureLeadingSlash(path: string | null | undefined): string {
  if (!path || path === '/') return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function appendSearchAndHash(
  path: string,
  search?: string | URLSearchParams | null,
  hash?: string | null,
): string {
  let result = path;
  const searchText =
    search instanceof URLSearchParams
      ? search.toString()
      : typeof search === 'string'
        ? search.replace(/^\?/, '')
        : '';
  if (searchText) result += `?${searchText}`;
  const hashText = typeof hash === 'string' ? hash.replace(/^#/, '') : '';
  if (hashText) result += `#${hashText}`;
  return result;
}

function parseUrlLike(url: string | URL): URL | null {
  try {
    return url instanceof URL ? url : new URL(url);
  } catch {
    return null;
  }
}

function decodePathSegment(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

export function normalizeStoreSlug(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || hasControlOrBidi(trimmed)) return null;
  if (trimmed !== trimmed.toLowerCase()) return null;
  if (!STORE_SLUG_PATTERN.test(trimmed)) return null;
  if (RESERVED_STOREFRONT_SUBDOMAIN_SET.has(trimmed)) return null;
  return trimmed;
}

export function isReservedStorefrontSubdomain(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return RESERVED_STOREFRONT_SUBDOMAIN_SET.has(value.trim().toLowerCase());
}

export function normalizeStorefrontBaseDomain(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || hasControlOrBidi(trimmed)) {
    throw new Error('Invalid storefront base domain');
  }

  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    const hostname = normalizeHostname(parsed.hostname);
    if (!hostname) throw new Error('Invalid storefront base domain');
    return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  } catch {
    throw new Error('Invalid storefront base domain');
  }
}

export function getStorefrontBaseDomainFromOrigin(publicStorefrontOrigin: string): string {
  return normalizeStorefrontBaseDomain(publicStorefrontOrigin);
}

export function extractLegacyStorefrontPath(pathname: string): {
  readonly slug: string;
  readonly pathWithoutStorePrefix: string;
} | null {
  const match = /^\/s\/([^/?#]+)(\/.*)?$/.exec(pathname);
  if (!match?.[1]) return null;
  const decodedSlug = decodePathSegment(match[1]);
  const slug = normalizeStoreSlug(decodedSlug);
  if (!slug) return null;
  return {
    slug,
    pathWithoutStorePrefix: match[2] && match[2] !== '' ? match[2] : '/',
  };
}

function hasLegacyStorefrontPathPrefix(pathname: string): boolean {
  return pathname === '/s' || pathname.startsWith('/s/');
}

export function resolveStorefrontAddress(input: ResolveStorefrontAddressInput): StorefrontAddress {
  const url = parseUrlLike(input.url);
  if (!url) return { kind: 'invalid' };

  const baseDomain = normalizeStorefrontBaseDomain(input.baseDomain);
  const hostname = normalizeHostname(url.hostname);
  if (!hostname) return { kind: 'invalid' };

  const legacy = extractLegacyStorefrontPath(url.pathname);
  const hasLegacyPathPrefix = hasLegacyStorefrontPathPrefix(url.pathname);
  const isPlatformHost = hostname === baseDomain || hostname === `www.${baseDomain}`;

  if (hasLegacyPathPrefix && !legacy) return { kind: 'invalid' };

  if (isPlatformHost) {
    return legacy
      ? {
          kind: 'legacy',
          slug: legacy.slug,
          pathWithoutStorePrefix: legacy.pathWithoutStorePrefix,
        }
      : { kind: 'platform' };
  }

  if (!hostname.endsWith(`.${baseDomain}`)) {
    return { kind: 'invalid' };
  }

  const label = hostname.slice(0, -(baseDomain.length + 1));
  if (!label || label.includes('.')) return { kind: 'invalid' };
  const slug = normalizeStoreSlug(label);
  if (!slug) return { kind: 'invalid' };

  if (legacy && legacy.slug !== slug) return { kind: 'invalid' };

  return {
    kind: 'tenant',
    slug,
    pathWithoutStorePrefix: legacy?.pathWithoutStorePrefix ?? url.pathname,
  };
}

export function buildTenantStorefrontUrl(
  origin: string,
  { storeSlug, path = '/', search, hash }: StorefrontUrlParts,
): string {
  const slug = normalizeStoreSlug(storeSlug);
  if (!slug) {
    throw new Error('Invalid storefront slug');
  }
  const base = new URL(origin);
  const baseDomain = getStorefrontBaseDomainFromOrigin(origin);
  const url = new URL(base.origin);
  url.hostname = `${slug}.${baseDomain}`;
  url.pathname = ensureLeadingSlash(path);
  const combined = appendSearchAndHash(url.toString(), search, hash);
  return combined;
}

export function buildLegacyStorefrontPath({
  storeSlug,
  path = '/',
  search,
  hash,
}: StorefrontUrlParts): string {
  const slug = normalizeStoreSlug(storeSlug);
  if (!slug) {
    throw new Error('Invalid storefront slug');
  }
  const suffix = ensureLeadingSlash(path);
  const legacyPath = `/s/${encodeURIComponent(slug)}${suffix === '/' ? '' : suffix}`;
  return appendSearchAndHash(legacyPath, search, hash);
}

export function buildRootStorefrontUrl(
  origin: string,
  input: Omit<StorefrontUrlParts, 'storeSlug'> & { readonly path?: string },
): string {
  const url = new URL(origin);
  url.pathname = ensureLeadingSlash(input.path);
  url.search = '';
  url.hash = '';
  return appendSearchAndHash(url.toString(), input.search, input.hash);
}
